import React, { useState, useRef } from "react";
import { TYPES } from "../../../constants/wineTypes";
import { Modal } from "../../../components/ui/Modal";
import { Pill } from "../../../components/ui/Pill";
import { generateChatContent, parseChatCsv } from "../chatPage.api";

interface CSVWine {
  id: number;
  name: string;
  mil: string;
  dom: string;
  type: string;
  prix: string;
  desc: string;
  gen: boolean;
}

interface CSVImportModalProps {
  onImport: (wines: CSVWine[]) => void;
  onClose: () => void;
}

export function CSVImportModal({ onImport, onClose }: CSVImportModalProps) {
  const [step, setStep] = useState<"paste" | "preview" | "generating" | "done">("paste");
  const [raw, setRaw] = useState("");
  const [wines, setWines] = useState<CSVWine[]>([]);
  const [prog, setProg] = useState({ done: 0, total: 0 });
  const [genResults, setGenResults] = useState<Record<number, string>>({});
  const fileRef = useRef<HTMLInputElement>(null);

  const parse = async () => {
    const parsed = await parseChatCsv(raw);
    const w: CSVWine[] = parsed.map((p: any, i: number) => ({ ...p, id: Date.now() + i }));
    if (!w.length) return;
    setWines(w);
    setStep("preview");
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = (ev) => { setRaw(ev.target?.result as string); };
    r.readAsText(f, "utf-8");
    e.target.value = "";
  };

  const generate = async () => {
    setStep("generating");
    setProg({ done: 0, total: wines.length });
    const res: Record<number, string> = {};
    for (let i = 0; i < wines.length; i++) {
      const w = wines[i];
      try {
        const result = await generateChatContent({
          tab: "fiche",
          wine: `${w.name} ${w.mil} ${w.dom}`,
          lang: "fr",
        });
        res[w.id] = result.text;
      } catch {
        res[w.id] = "Erreur.";
      }
      setProg({ done: i + 1, total: wines.length });
    }
    setGenResults(res);
    setStep("done");
  };

  const confirm = () => {
    onImport(wines.map((w) => ({ ...w, desc: genResults[w.id] || "" })));
    onClose();
  };

  return (
    <Modal onClose={onClose} title="Import CSV — Batch génération" wide>
      {step === "paste" && (
        <>
          <div className="mb-2.5">
            <div className="text-xs tracking-[2px] text-tertiary uppercase mb-2">Coller votre liste de vins</div>
            <textarea
              value={raw} onChange={(e) => setRaw(e.target.value)} rows={7}
              placeholder={`nom,millesime,domaine,type,prix\nGevrey-Chambertin,2019,Rossignol-Trapet,rouge,68`}
              className="input font-mono text-[12px] resize-y leading-[1.6]"
            />
          </div>
          <div className="flex gap-2 mb-2.5">
            <button
              onClick={() => fileRef.current?.click()}
              className="btn-secondary flex-1 py-2 text-sm"
            >Importer un fichier CSV</button>
            <Pill active onClick={parse} disabled={!raw.trim()} style={{ flex: 2 }}>Analyser la liste →</Pill>
          </div>
          <input ref={fileRef} type="file" accept=".csv,.txt" onChange={handleFile} className="hidden" />
        </>
      )}
      {step === "preview" && (
        <>
          <div className="text-sm text-secondary mb-2.5">{wines.length} vins détectés</div>
          <div className="max-h-[200px] overflow-y-auto mb-3 border border-border rounded-md">
            {wines.map((w, i) => (
              <div key={w.id} className={`flex gap-2.5 px-3 py-2 items-center ${i < wines.length - 1 ? "border-b border-foreground/[0.04]" : ""}`}>
                <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: TYPES.find((t) => t.id === w.type)?.color || "currentColor" }} />
                <div className="flex-1 text-[12px] text-foreground font-light">{w.name} <span className="text-tertiary">{w.mil}</span></div>
                <div className="text-sm text-gold shrink-0">{w.prix ? `${w.prix}€` : ""}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Pill onClick={() => setStep("paste")} style={{ flex: 1 }}>← Modifier</Pill>
            <Pill active onClick={generate} style={{ flex: 2 }}>Générer {wines.length} fiches →</Pill>
          </div>
        </>
      )}
      {step === "generating" && (
        <div className="py-5 text-center">
          <div className="text-base text-secondary mb-4 italic">
            {prog.done < prog.total ? `Génération ${prog.done} / ${prog.total}…` : "Finalisation…"}
          </div>
          <div className="h-[3px] bg-surface-2 rounded-sm overflow-hidden mb-2">
            <div className="h-full bg-accent rounded-sm transition-[width] duration-300" style={{ width: `${prog.total > 0 ? Math.round((prog.done / prog.total) * 100) : 0}%` }} />
          </div>
        </div>
      )}
      {step === "done" && (
        <>
          <div className="text-[12px] text-success mb-3 flex items-center gap-2">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <circle cx="7" cy="7" r="6" className="fill-success/15" />
              <path d="M4 7.2L6.2 9.5L10 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {wines.length} fiches générées
          </div>
          <Pill active onClick={confirm} style={{ width: "100%" }}>Ajouter à la cave →</Pill>
        </>
      )}
    </Modal>
  );
}
